import { FC, useState, useEffect } from 'react';
import { Box, Grid, Typography, Button } from '@mui/material';
import { ArrowBack } from '@mui/icons-material';
import {
  useRoutesAndScheduleState,
  useRoutesAndScheduleActions,
  EViewState,
} from '../../contexts/routesAndSchedule.context';
import RoutesAndScheduleGrid from './routesAndScheduleGrid.view';
import { useTranslation } from 'src/hooks/useTranslationHook';
import {
  TDriver,
  TVehicle,
  TRoute,
  TRouteAssignment,
  TViewType,
  TRowClickHandlerType,
} from 'src/apis/administration/administration.interfaces';
import SelectedRouteDetailsView from './details/selectedRouteDetails.view';
import SelectedVehicleDetailsView from './details/selectedVehicleDetails.view';
import SelectedAssignmentDetailsView from './details/selectAssignmentDetails.view';
import SelectedSalaryDetailsView from './details/selectedSalaryDetails.view';
import { AddButton, EditButton } from 'src/views/components/simple/AllButtons';
import AddEditAssignmentDetailsView from './manage/addEditAssignmentDetails.view';
import AddEditRouteDetailsView from './manage/addEditRouteDetails.view';
import AddEditSalaryDetailsView from './manage/addEditSalaryDetails.view';
import AddEditVehicleDetailsView from './manage/addEditVehicleDetails.view';

const RoutesAndScheduleView: FC = () => {
  const { translateText } = useTranslation();
  const {
    routes,
    vehicles,
    routeAssignments,
    drivers,
    isLoading,
    selectedRoute,
    selectedVehicle,
    selectedAssignment,
    selectedDriver,
    editMode,
    viewState,
  } = useRoutesAndScheduleState();
  const {
    selectRoute,
    selectVehicle,
    selectAssignment,
    selectDriver,
    setEditMode,
    setViewType,
    setViewState,
  } = useRoutesAndScheduleActions();

  const [currentViewType, setCurrentViewType] = useState<TViewType | null>(
    null
  );

  useEffect(() => {
    if (viewState === EViewState.EDIT && !editMode) {
      setViewState(EViewState.DETAILS);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editMode, viewState]);

  const onRoutesRowClick = (data: TRowClickHandlerType) => {
    selectRoute(data as TRoute);
    setCurrentViewType(TViewType.ROUTES);
    setViewState(EViewState.DETAILS);
  };

  const onVehiclesRowClick = (data: TRowClickHandlerType) => {
    selectVehicle(data as TVehicle);
    setCurrentViewType(TViewType.VEHICLES);
    setViewState(EViewState.DETAILS);
  };

  const onAssignmentsRowClick = (data: TRowClickHandlerType) => {
    selectAssignment(data as TRouteAssignment);
    setCurrentViewType(TViewType.ASSIGNMENTS);
    setViewState(EViewState.DETAILS);
  };

  const onDriversRowClick = (data: TRowClickHandlerType) => {
    selectDriver(data as TDriver);
    setCurrentViewType(TViewType.SALARIES);
    setViewState(EViewState.DETAILS);
  };

  const handleEditClickHandler = () => {
    if (currentViewType) {
      setEditMode('edit');
      setViewType(currentViewType);
      setViewState(EViewState.EDIT);
    }
  };

  const handleBackToList = () => {
    setViewState(EViewState.LIST);
    setCurrentViewType(null);
    selectRoute(undefined);
    selectVehicle(undefined);
    selectAssignment(undefined);
    selectDriver(undefined);
  };

  const handleAddRouteClickHandler = () => {
    selectRoute(undefined);
    setEditMode('create');
    setViewType(TViewType.ROUTES);
  };

  const handleAddVehicleClickHandler = () => {
    selectVehicle(undefined);
    setEditMode('create');
    setViewType(TViewType.VEHICLES);
  };

  const handleAddAssignmentClickHandler = () => {
    selectAssignment(undefined);
    setEditMode('create');
    setViewType(TViewType.ASSIGNMENTS);
  };

  const handleAddDriverClickHandler = () => {
    selectDriver(undefined);
    setEditMode('create');
    setViewType(TViewType.SALARIES);
  };

  if (isLoading) {
    return (
      <Box sx={{ p: { xs: 1, sm: 2, md: 3 } }} data-testid="routes-schedule-loading">
        <Typography
          sx={{
            fontSize: { xs: '0.875rem', sm: '1rem' },
          }}
        >
          {translateText('administration.routesAndSchedule.loading')}
        </Typography>
      </Box>
    );
  }

  // Check if data is loaded
  if (
    !routes.length &&
    !vehicles.length &&
    !routeAssignments.length &&
    !drivers.length
  ) {
    return (
      <Box sx={{ p: { xs: 1, sm: 2, md: 3 } }} data-testid="routes-schedule-no-data">
        <Typography
          sx={{
            fontSize: { xs: '0.875rem', sm: '1rem' },
          }}
        >
          {translateText('administration.routesAndSchedule.noData')}
        </Typography>
      </Box>
    );
  }

  const getEditLabel = () => {
    switch (currentViewType) {
      case TViewType.ROUTES:
        return translateText('administration.routesAndSchedule.editRoute');
      case TViewType.VEHICLES:
        return translateText('administration.routesAndSchedule.editVehicle');
      case TViewType.ASSIGNMENTS:
        return translateText('administration.routesAndSchedule.editAssignment');
      case TViewType.SALARIES:
        return translateText('administration.routesAndSchedule.editDriver');
      default:
        return translateText('administration.routesAndSchedule.editRoute');
    }
  };

  const renderDetailsView = () => {
    if (currentViewType === TViewType.ROUTES && selectedRoute) {
      return <SelectedRouteDetailsView />;
    }
    if (currentViewType === TViewType.VEHICLES && selectedVehicle) {
      return <SelectedVehicleDetailsView />;
    }
    if (currentViewType === TViewType.ASSIGNMENTS && selectedAssignment) {
      return <SelectedAssignmentDetailsView />;
    }
    if (currentViewType === TViewType.SALARIES && selectedDriver) {
      return <SelectedSalaryDetailsView />;
    }
    return null;
  };

  return (
    <Box
      sx={{ width: '100%', height: '100%', overflow: 'auto' }}
      data-testid="routes-schedule-container"
    >
      {(viewState === EViewState.DETAILS || viewState === EViewState.EDIT) && (
        <Box
          sx={{
            mb: { xs: 1, sm: 2 },
            display: 'flex',
            flexDirection: { xs: 'column', sm: 'row' },
            justifyContent: 'space-between',
            alignItems: { xs: 'stretch', sm: 'center' },
            gap: { xs: 1, sm: 0 },
          }}
          data-testid="routes-schedule-details-header"
        >
          <Button
            startIcon={<ArrowBack />}
            onClick={handleBackToList}
            sx={{
              mb: { xs: 0, sm: 2 },
              minHeight: { xs: '44px', sm: '36px' },
              fontSize: { xs: '16px', sm: '14px' },
              width: { xs: '100%', sm: 'auto' },
            }}
            data-testid="back-to-list-button"
          >
            {translateText('administration.routesAndSchedule.backToList')}
          </Button>
          {viewState === EViewState.DETAILS && (
            <EditButton
              label={getEditLabel()}
              onClickHandler={handleEditClickHandler}
              data-testid="edit-route-button"
            />
          )}
        </Box>
      )}

      {viewState === EViewState.DETAILS || viewState === EViewState.EDIT ? (
        <Box data-testid="routes-schedule-details-container">
          {renderDetailsView()}
        </Box>
      ) : (
        <>
          {/* Summary Statistics */}
          <Grid
            container
            spacing={{ xs: 1, sm: 2 }}
            sx={{ my: { xs: 2, sm: 4 } }}
            data-testid="routes-schedule-summary-stats"
          >
            <Grid item xs={12} sm={6} md={4} lg={3}>
              <Box
                sx={{
                  p: { xs: 1.5, sm: 2 },
                  bgcolor: 'primary.light',
                  borderRadius: 1,
                  textAlign: 'center',
                }}
                data-testid="total-routes-card"
              >
                <Typography
                  variant="h6"
                  color="white"
                  sx={{
                    fontSize: { xs: '1rem', sm: '1.25rem' },
                  }}
                >
                  {routes.length}
                </Typography>
                <Typography
                  variant="body2"
                  color="white"
                  sx={{
                    fontSize: { xs: '0.75rem', sm: '0.875rem' },
                  }}
                >
                  {translateText(
                    'administration.routesAndSchedule.totalRoutes'
                  )}
                </Typography>
              </Box>
            </Grid>
            <Grid item xs={12} sm={6} md={4} lg={3}>
              <Box
                sx={{
                  p: { xs: 1.5, sm: 2 },
                  bgcolor: 'secondary.light',
                  borderRadius: 1,
                  textAlign: 'center',
                }}
                data-testid="total-vehicles-card"
              >
                <Typography
                  variant="h6"
                  color="white"
                  sx={{
                    fontSize: { xs: '1rem', sm: '1.25rem' },
                  }}
                >
                  {vehicles.length}
                </Typography>
                <Typography
                  variant="body2"
                  color="white"
                  sx={{
                    fontSize: { xs: '0.75rem', sm: '0.875rem' },
                  }}
                >
                  {translateText(
                    'administration.routesAndSchedule.totalVehicles'
                  )}
                </Typography>
              </Box>
            </Grid>
            <Grid item xs={12} sm={6} md={4} lg={3}>
              <Box
                sx={{
                  p: { xs: 1.5, sm: 2 },
                  bgcolor: 'success.light',
                  borderRadius: 1,
                  textAlign: 'center',
                }}
                data-testid="total-drivers-card"
              >
                <Typography
                  variant="h6"
                  color="white"
                  sx={{
                    fontSize: { xs: '1rem', sm: '1.25rem' },
                  }}
                >
                  {drivers.length}
                </Typography>
                <Typography
                  variant="body2"
                  color="white"
                  sx={{
                    fontSize: { xs: '0.75rem', sm: '0.875rem' },
                  }}
                >
                  {translateText(
                    'administration.routesAndSchedule.totalDrivers'
                  )}
                </Typography>
              </Box>
            </Grid>
            <Grid item xs={12} sm={6} md={4} lg={3}>
              <Box
                sx={{
                  p: { xs: 1.5, sm: 2 },
                  bgcolor: 'info.light',
                  borderRadius: 1,
                  textAlign: 'center',
                }}
                data-testid="active-assignments-card"
              >
                <Typography
                  variant="h6"
                  color="white"
                  sx={{
                    fontSize: { xs: '1rem', sm: '1.25rem' },
                  }}
                >
                  {routeAssignments.length}
                </Typography>
                <Typography
                  variant="body2"
                  color="white"
                  sx={{
                    fontSize: { xs: '0.75rem', sm: '0.875rem' },
                  }}
                >
                  {translateText(
                    'administration.routesAndSchedule.activeAssignments'
                  )}
                </Typography>
              </Box>
            </Grid>
            <Grid item xs={12} sm={6} md={4} lg={3}>
              <Box
                sx={{
                  p: { xs: 1.5, sm: 2 },
                  bgcolor: 'warning.light',
                  borderRadius: 1,
                  textAlign: 'center',
                }}
                data-testid="pending-salaries-card"
              >
                <Typography
                  variant="h6"
                  color="white"
                  sx={{
                    fontSize: { xs: '1rem', sm: '1.25rem' },
                  }}
                >
                  {
                    drivers.filter(
                      (d) =>
                        d.salaryStatus === 'PENDING' ||
                        d.salaryStatus === 'OVERDUE'
                    ).length
                  }
                </Typography>
                <Typography
                  variant="body2"
                  color="white"
                  sx={{
                    fontSize: { xs: '0.75rem', sm: '0.875rem' },
                  }}
                >
                  {translateText(
                    'administration.routesAndSchedule.pendingSalaries'
                  )}
                </Typography>
              </Box>
            </Grid>
          </Grid>

          <Grid
            container
            spacing={{ xs: 1, sm: 2, md: 3 }}
            data-testid="routes-schedule-grids-container"
          >
            <Grid item xs={12} data-testid="routes-overview-section">
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: { xs: 'column', sm: 'row' },
                  justifyContent: 'space-between',
                  alignItems: { xs: 'stretch', sm: 'center' },
                  mb: { xs: 1, sm: 2 },
                  gap: { xs: 1, sm: 0 },
                }}
              >
                <Typography
                  variant="h5"
                  gutterBottom
                  sx={{
                    fontSize: { xs: '1.25rem', sm: '1.5rem', md: '1.75rem' },
                    mb: { xs: 0, sm: 1 },
                  }}
                >
                  {translateText(
                    'administration.routesAndSchedule.routesOverview'
                  )}
                </Typography>
                <AddButton
                  label={translateText(
                    'administration.routesAndSchedule.addRoute'
                  )}
                  onClickHandler={handleAddRouteClickHandler}
                  data-testid="add-route-button"
                />
              </Box>
              <RoutesAndScheduleGrid
                viewType={TViewType.ROUTES}
                onRowClick={onRoutesRowClick}
              />
            </Grid>

            <Grid item xs={12} data-testid="vehicles-drivers-section">
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: { xs: 'column', sm: 'row' },
                  justifyContent: 'space-between',
                  alignItems: { xs: 'stretch', sm: 'center' },
                  mb: { xs: 1, sm: 2 },
                  gap: { xs: 1, sm: 0 },
                }}
              >
                <Typography
                  variant="h5"
                  gutterBottom
                  sx={{
                    fontSize: { xs: '1.25rem', sm: '1.5rem', md: '1.75rem' },
                    mb: { xs: 0, sm: 1 },
                  }}
                >
                  {translateText(
                    'administration.routesAndSchedule.vehiclesAndDrivers'
                  )}
                </Typography>
                <AddButton
                  label={translateText(
                    'administration.routesAndSchedule.addVehicle'
                  )}
                  onClickHandler={handleAddVehicleClickHandler}
                  data-testid="add-vehicle-button"
                />
              </Box>
              <RoutesAndScheduleGrid
                viewType={TViewType.VEHICLES}
                onRowClick={onVehiclesRowClick}
              />
            </Grid>

            <Grid item xs={12} data-testid="route-assignments-section">
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: { xs: 'column', sm: 'row' },
                  justifyContent: 'space-between',
                  alignItems: { xs: 'stretch', sm: 'center' },
                  mb: { xs: 1, sm: 2 },
                  gap: { xs: 1, sm: 0 },
                }}
              >
                <Typography
                  variant="h5"
                  gutterBottom
                  sx={{
                    fontSize: { xs: '1.25rem', sm: '1.5rem', md: '1.75rem' },
                    mb: { xs: 0, sm: 1 },
                  }}
                >
                  {translateText(
                    'administration.routesAndSchedule.routeAssignments'
                  )}
                </Typography>
                <AddButton
                  label={translateText(
                    'administration.routesAndSchedule.addAssignment'
                  )}
                  onClickHandler={handleAddAssignmentClickHandler}
                  data-testid="add-assignment-button"
                />
              </Box>
              <RoutesAndScheduleGrid
                viewType={TViewType.ASSIGNMENTS}
                onRowClick={onAssignmentsRowClick}
              />
            </Grid>

            <Grid item xs={12} data-testid="driver-salaries-section">
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: { xs: 'column', sm: 'row' },
                  justifyContent: 'space-between',
                  alignItems: { xs: 'stretch', sm: 'center' },
                  mb: { xs: 1, sm: 2 },
                  gap: { xs: 1, sm: 0 },
                }}
              >
                <Typography
                  variant="h5"
                  gutterBottom
                  sx={{
                    fontSize: { xs: '1.25rem', sm: '1.5rem', md: '1.75rem' },
                    mb: { xs: 0, sm: 1 },
                  }}
                >
                  {translateText(
                    'administration.routesAndSchedule.driverSalaries'
                  )}
                </Typography>
                <AddButton
                  label={translateText(
                    'administration.routesAndSchedule.addDriver'
                  )}
                  onClickHandler={handleAddDriverClickHandler}
                  data-testid="add-driver-button"
                />
              </Box>
              <RoutesAndScheduleGrid
                viewType={TViewType.SALARIES}
                onRowClick={onDriversRowClick}
              />
            </Grid>
          </Grid>
        </>
      )}
      <AddEditAssignmentDetailsView />
      <AddEditRouteDetailsView />
      <AddEditSalaryDetailsView />
      <AddEditVehicleDetailsView />
    </Box>
  );
};

export default RoutesAndScheduleView;
